import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaGlobe, FaMapMarkerAlt, FaShieldAlt, FaSignOutAlt, FaTrash, FaUser } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import i18n, { fetchLanguages } from "../../i18n";

const fallbackLanguages = [
  { code: "en", label: "English", langId: "2224" },
  { code: "id", label: "Bahasa Indonesia", langId: "2225" },
  { code: "cn", label: "Chinese", langId: "2226" },
];

const getLanguageCode = (language) => {
  const lookupDetail = language?.LookupDetail || "";
  const matchedCode = lookupDetail.match(/^(\S+)/)?.[1]?.toLowerCase() || "";
  if (matchedCode.startsWith("zh")) return "cn";
  if (matchedCode) return matchedCode;

  const id = String(language?.Id || language?.id || "");
  if (id === "2224") return "en";
  if (id === "2225") return "id";
  if (id === "2226") return "cn";
  return "";
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("language") || "en",
  );
  const [availableLanguages, setAvailableLanguages] = useState(fallbackLanguages);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressView, setAddressView] = useState("list"); // 'list' | 'form'
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [addressUid, setAddressUid] = useState("");
  const [addressForm, setAddressForm] = useState({
    contact: "",
    block: "",
    street: "",
    unitNo: "",
    postal: "",
  });

  const emptyAddressForm = { contact: "", block: "", street: "", unitNo: "", postal: "" };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadLanguages = async () => {
      setIsLoadingLanguages(true);
      try {
        const languages = await fetchLanguages();
        if (!mounted || !Array.isArray(languages) || !languages.length) {
          return;
        }

        const mapped = languages
          .map((language) => {
            const code = getLanguageCode(language);
            if (!code) return null;

            return {
              code,
              label: language?.LookupDesc || code.toUpperCase(),
              langId: String(language?.Id || language?.id || ""),
            };
          })
          .filter(Boolean);

        const unique = [];
        const seenCodes = new Set();

        mapped.forEach((language) => {
          if (!seenCodes.has(language.code)) {
            seenCodes.add(language.code);
            unique.push(language);
          }
        });

        if (unique.length) {
          setAvailableLanguages(unique);
        }
      } catch (error) {
        console.error("Failed to load settings languages:", error);
      } finally {
        if (mounted) {
          setIsLoadingLanguages(false);
        }
      }
    };

    loadLanguages();

    return () => {
      mounted = false;
    };
  }, []);

  const currentLanguageLabel = useMemo(() => {
    const found = availableLanguages.find((language) => language.code === selectedLanguage);
    return found?.label || selectedLanguage.toUpperCase();
  }, [availableLanguages, selectedLanguage]);

  const goToProfile = () => {
    navigate("/account", { state: { setactiveTab: "personalProfile" } });
  };

  const goToSecurity = () => {
    navigate("/account-security");
  };

  const fetchAddressList = async () => {
    const custId = localStorage.getItem("userID");
    if (!custId) return;

    setIsLoadingAddress(true);
    try {
      const res = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/UserAddress/List?custId=${custId}`,
      );
      const data = await res.json();
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setAddressList(list);
    } catch (error) {
      console.error("Failed to load saved addresses:", error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const openAddressModal = () => {
    setShowAddressModal(true);
    setAddressView("list");
    fetchAddressList();
  };

  const openAddAddress = () => {
    setAddressUid("");
    setAddressForm(emptyAddressForm);
    setAddressView("form");
  };

  const openEditAddress = (address) => {
    setAddressUid(address.Uid || "");
    setAddressForm({
      contact: address.Contact || "",
      block: address.Block || "",
      street: address.Street || "",
      unitNo: address.UnitNo || "",
      postal: address.Postal || "",
    });
    setAddressView("form");
  };

  const handleSaveAddress = async () => {
    const custId = localStorage.getItem("userID");
    if (!custId) return;

    setIsSavingAddress(true);
    const payload = {
      Uid: addressUid || "",
      CustId: Number(custId) || 0,
      Contact: addressForm.contact,
      Block: addressForm.block,
      Street: addressForm.street,
      UnitNo: addressForm.unitNo,
      Postal: addressForm.postal,
      Country: "Singapore",
    };

    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/UserAddress/UpdateAddress",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const text = await res.text();
      if (!res.ok) {
        console.error("Failed to save address:", res.status, text);
        alert(t("account.addressSaveError", { defaultValue: "Failed to save address. Please try again." }));
        return;
      }
      await fetchAddressList();
      setAddressView("list");
    } catch (error) {
      console.error("Error saving address:", error);
      alert(t("account.addressSaveError", { defaultValue: "Failed to save address. Please try again." }));
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (address) => {
    const custId = localStorage.getItem("userID");
    if (!custId || !address?.Uid) return;

    const confirmed = window.confirm(
      t("account.confirmDeleteAddress", {
        defaultValue: "Are you sure you want to delete this address?",
      }),
    );
    if (!confirmed) return;

    setIsDeletingAddress(true);
    const payload = [
      {
        Uid: address.Uid,
        CustId: Number(custId) || 0,
        Contact: "",
        Block: "",
        Street: "",
        UnitNo: "",
        Postal: "",
        Country: "",
        Default: 0,
      },
    ];

    try {
      const res = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/StoreAddress/DeleteAddress?Uid=${encodeURIComponent(address.Uid)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const text = await res.text();
      if (!res.ok) {
        console.error("Failed to delete address:", res.status, text);
        alert(t("account.addressDeleteError", { defaultValue: "Failed to delete address. Please try again." }));
        return;
      }
      await fetchAddressList();
      if (addressUid === address.Uid) {
        setAddressView("list");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      alert(t("account.addressDeleteError", { defaultValue: "Failed to delete address. Please try again." }));
    } finally {
      setIsDeletingAddress(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userID");
    localStorage.removeItem("username");
    localStorage.removeItem("token");
    navigate("/main", { replace: true });
  };

  // This screen previously changed the language locally and reloaded without ever telling the
  // server. `syncProfileLanguageFromGetProfile` then restored the profile's language on load,
  // so the choice reverted every time. Mirrors the same call in TopBar and LanguageSettings.
  const updateUserLanguage = async (langId, langDesc) => {
    try {
      const userId = localStorage.getItem("userID");
      if (!userId) return true; // Signed out — nothing to persist server-side.
      const response = await fetch("https://xianhefairy.com/LotusFairy_API/api/User/LanguageUpdate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ID: parseInt(userId), LANG_ID: langId, LANG_DESC: langDesc }),
      });
      if (!response.ok) console.error("Language update rejected:", response.status);
      return response.ok;
    } catch (error) {
      console.error("Error updating user language:", error);
      return false;
    }
  };

  const handleLanguageSelect = async (language) => {
    if (!language?.code) return;

    setSelectedLanguage(language.code);
    localStorage.setItem("language", language.code);
    // Kept in sync with `language` — LanguageSettings reads this key to decide its ticked row.
    localStorage.setItem("profileLanguage", language.code);
    if (language.langId) {
      localStorage.setItem("languageId", language.langId);
    }

    await i18n.changeLanguage(language.code);
    setShowLanguageModal(false);

    // Await the save before reloading, otherwise the profile sync wins on the way back up.
    await updateUserLanguage(
      language.langId ? parseInt(language.langId) : null,
      language.label || language.name || language.code,
    );
    window.location.reload();
  };

  const settingItems = [
    {
      key: "profile",
      label: t("account.personalProfile", { defaultValue: "Personal Profile" }),
      icon: <FaUser size={16} color="#deaf77" />,
      action: goToProfile,
    },
    {
      key: "security",
      label: t("account.accountSecurity", { defaultValue: "Account Security" }),
      icon: <FaShieldAlt size={16} color="#deaf77" />,
      action: goToSecurity,
    },
    {
      key: "address",
      label: t("account.deliveryAddress", { defaultValue: "Delivery Address" }),
      icon: <FaMapMarkerAlt size={16} color="#deaf77" />,
      action: openAddressModal,
    },
    {
      key: "language",
      label: t("account.language", { defaultValue: "Language" }),
      icon: <FaGlobe size={16} color="#deaf77" />,
      action: () => navigate("/language-settings"),
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: isMobile ? "20px 14px 120px" : "34px 20px 160px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "680px",
          margin: "0 auto",
          borderRadius: "18px",
          border: "2px solid rgba(201,168,75,0.4)",
          background: "#0A031E",
          boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
          padding: isMobile ? "18px" : "24px",
          fontFamily: "DMSans, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
            gap: "8px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            aria-label={t("account.back", { defaultValue: "Back" })}
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              border: "1px solid rgba(201,168,75,0.5)",
              background: "transparent",
              color: "#deaf77",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <FaChevronLeft size={14} />
          </button>

          <h1
            style={{
              margin: 0,
              color: "#deaf77",
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
              fontSize: isMobile ? "24px" : "28px",
            }}
          >
            {t("account.settings", { defaultValue: "Settings" })}
          </h1>
        </div>

        <div style={{ display: "grid", gap: "12px" }}>
          {settingItems.map((item) => (
            <button
              key={item.key}
              onClick={item.action}
              style={{
                width: "100%",
                borderRadius: "14px",
                border: "1px solid rgba(201,168,75,0.4)",
                background: "#0A031E",
                color: "#fff2d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: isMobile ? "14px 14px" : "15px 16px",
                cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {item.icon}
                <span style={{ fontSize: isMobile ? "15px" : "16px", textAlign: "left" }}>
                  {item.label}
                </span>
              </span>

              <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {item.value ? (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#deaf77",
                      border: "1px solid rgba(201,168,75,0.4)",
                      background: "rgba(201,168,75,0.12)",
                      borderRadius: "999px",
                      padding: "4px 10px",
                    }}
                  >
                    {item.value}
                  </span>
                ) : null}
                <FaChevronRight size={13} color="#deaf77" />
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          style={{
            marginTop: "22px",
            width: "100%",
            borderRadius: "999px",
            border: "1px solid rgba(255,95,95,0.65)",
            background: "linear-gradient(90deg, rgba(102,17,24,0.62), rgba(45,10,12,0.72))",
            color: "#FF665E",
            padding: "14px",
            cursor: isLoggingOut ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontFamily: "DMSans-Bold, DMSans, sans-serif",
            fontSize: "16px",
          }}
        >
          <FaSignOutAlt size={16} />
          {isLoggingOut
            ? t("account.loggingOut", { defaultValue: "Logging out..." })
            : t("tryoutpage.logout", { defaultValue: "Log out" })}
        </button>
      </div>

      {showLanguageModal && (
        <div
          onClick={() => setShowLanguageModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "14px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: "#171520",
              padding: "16px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2
                style={{
                  margin: 0,
                  fontFamily: "DMSans-Bold, DMSans, sans-serif",
                  fontSize: "20px",
                }}
              >
                {t("account.selectLanguage", { defaultValue: "Select Language" })}
              </h2>
              <button
                onClick={() => setShowLanguageModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#deaf77",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("account.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div style={{ marginTop: "14px", display: "grid", gap: "8px" }}>
              {isLoadingLanguages ? (
                <p style={{ margin: 0, color: "#D8CCA8" }}>
                  {t("account.loading", { defaultValue: "Loading..." })}
                </p>
              ) : (
                availableLanguages.map((language) => {
                  const active = selectedLanguage === language.code;
                  return (
                    <button
                      key={`${language.code}-${language.langId}`}
                      onClick={() => handleLanguageSelect(language)}
                      style={{
                        width: "100%",
                        borderRadius: "10px",
                        border: active
                          ? "1px solid #deaf77"
                          : "1px solid rgba(201,168,75,0.4)",
                        background: active
                          ? "rgba(201,168,75,0.14)"
                          : "#0A031E",
                        color: "#fff2d2",
                        textAlign: "left",
                        padding: "10px 12px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "15px",
                      }}
                    >
                      <span>{language.label}</span>
                      {active ? <span>{t("account.selected", { defaultValue: "Selected" })}</span> : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {showAddressModal && (
        <div
          onClick={() => setShowAddressModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "14px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: "#171520",
              padding: "16px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {addressView === "form" && (
                  <button
                    onClick={() => setAddressView("list")}
                    aria-label={t("account.back", { defaultValue: "Back" })}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#deaf77",
                      cursor: "pointer",
                      display: "grid",
                      placeItems: "center",
                      padding: 0,
                    }}
                  >
                    <FaChevronLeft size={16} />
                  </button>
                )}
                <h2
                  style={{
                    margin: 0,
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "20px",
                  }}
                >
                  {addressView === "form"
                    ? addressUid
                      ? t("account.editAddress", { defaultValue: "Edit Address" })
                      : t("account.addAddress", { defaultValue: "Add Address" })
                    : t("account.deliveryAddress", { defaultValue: "Delivery Address" })}
                </h2>
              </div>
              <button
                onClick={() => setShowAddressModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#deaf77",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("account.close", { defaultValue: "Close" })}
              </button>
            </div>

            {isLoadingAddress ? (
              <p style={{ margin: "14px 0 0", color: "#D8CCA8" }}>
                {t("account.loading", { defaultValue: "Loading..." })}
              </p>
            ) : addressView === "list" ? (
              <div style={{ marginTop: "14px", display: "grid", gap: "10px" }}>
                {addressList.length === 0 ? (
                  <p style={{ margin: 0, color: "#D8CCA8", fontSize: "14px" }}>
                    {t("account.noSavedAddress", { defaultValue: "No saved addresses yet." })}
                  </p>
                ) : (
                  addressList.map((address, index) => (
                    <div
                      key={address.Uid || index}
                      style={{
                        width: "100%",
                        borderRadius: "10px",
                        border: "1px solid rgba(201,168,75,0.4)",
                        background: "#0A031E",
                        color: "#fff2d2",
                        padding: "12px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        onClick={() => openEditAddress(address)}
                        style={{
                          flex: 1,
                          textAlign: "left",
                          border: "none",
                          background: "transparent",
                          color: "#fff2d2",
                          padding: 0,
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span>
                          <span style={{ display: "block", fontWeight: 700, fontSize: "14px" }}>
                            {address.Contact || t("account.addressContact", { defaultValue: "Contact Name" })}
                          </span>
                          <span style={{ display: "block", fontSize: "13px", color: "rgba(255,242,210,0.75)", marginTop: "2px" }}>
                            {[address.Block, address.Street].filter(Boolean).join(" ")}
                            {address.UnitNo ? `, ${address.UnitNo}` : ""}
                            {address.Postal ? `, ${address.Postal}` : ""}
                            {address.Country ? `, ${address.Country}` : ""}
                          </span>
                        </span>
                        <FaChevronRight size={13} color="#deaf77" />
                      </button>
                      <button
                        onClick={() => handleDeleteAddress(address)}
                        disabled={isDeletingAddress}
                        aria-label={t("account.deleteAddress", { defaultValue: "Delete address" })}
                        style={{
                          border: "1px solid rgba(255,95,95,0.5)",
                          background: "rgba(255,95,95,0.1)",
                          color: "#FF665E",
                          borderRadius: "8px",
                          width: "32px",
                          height: "32px",
                          display: "grid",
                          placeItems: "center",
                          cursor: isDeletingAddress ? "not-allowed" : "pointer",
                          flexShrink: 0,
                          opacity: isDeletingAddress ? 0.6 : 1,
                        }}
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  ))
                )}

                <button
                  onClick={openAddAddress}
                  style={{
                    marginTop: "6px",
                    width: "100%",
                    borderRadius: "999px",
                    border: "1px solid rgba(201,168,75,0.5)",
                    background: "transparent",
                    color: "#deaf77",
                    padding: "12px",
                    cursor: "pointer",
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "15px",
                  }}
                >
                  + {t("account.addAddress", { defaultValue: "Add Address" })}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: "14px", display: "grid", gap: "10px" }}>
                {[
                  { key: "contact", label: t("account.addressContact", { defaultValue: "Contact Name" }) },
                  { key: "block", label: t("account.addressBlock", { defaultValue: "Block / Building" }) },
                  { key: "street", label: t("account.addressStreet", { defaultValue: "Street Name" }) },
                  { key: "unitNo", label: t("account.addressUnit", { defaultValue: "Unit No. (optional)" }) },
                  { key: "postal", label: t("account.addressPostal", { defaultValue: "Postal Code" }) },
                ].map((field) => (
                  <div key={field.key}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "12px",
                        color: "rgba(255,242,210,0.7)",
                      }}
                    >
                      {field.label}
                    </label>
                    <input
                      type="text"
                      value={addressForm[field.key]}
                      onChange={(e) =>
                        setAddressForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(201,168,75,0.4)",
                        background: "#0A031E",
                        color: "#fff2d2",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>
                ))}

                <button
                  onClick={handleSaveAddress}
                  disabled={isSavingAddress}
                  style={{
                    marginTop: "6px",
                    width: "100%",
                    borderRadius: "999px",
                    border: "none",
                    background: "#deaf77",
                    color: "#171520",
                    padding: "12px",
                    cursor: isSavingAddress ? "not-allowed" : "pointer",
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "15px",
                    opacity: isSavingAddress ? 0.7 : 1,
                  }}
                >
                  {isSavingAddress
                    ? t("account.saving", { defaultValue: "Saving..." })
                    : t("account.saveAddress", { defaultValue: "Save Address" })}
                </button>

                {addressUid && (
                  <button
                    onClick={() =>
                      handleDeleteAddress(
                        addressList.find((address) => address.Uid === addressUid) || { Uid: addressUid },
                      )
                    }
                    disabled={isDeletingAddress}
                    style={{
                      width: "100%",
                      borderRadius: "999px",
                      border: "1px solid rgba(255,95,95,0.65)",
                      background: "transparent",
                      color: "#FF665E",
                      padding: "12px",
                      cursor: isDeletingAddress ? "not-allowed" : "pointer",
                      fontFamily: "DMSans-Bold, DMSans, sans-serif",
                      fontSize: "15px",
                      opacity: isDeletingAddress ? 0.7 : 1,
                    }}
                  >
                    {isDeletingAddress
                      ? t("account.deleting", { defaultValue: "Deleting..." })
                      : t("account.deleteAddress", { defaultValue: "Delete Address" })}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;